import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiTeamLine,
  RiArchiveLine,
  RiSearchLine,
  RiErrorWarningLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from 'react-icons/ri';
import SurveyTableLoader from '../../survey/surveyDetails/components/SurveyTableLoader';
import NoData from '../../../components/NoData';
import PersonInfo from '../../../components/PersonInfo';
import { defaultRowsPerPage, getFormattedDateWithTime } from '../../../Utlils';
import {
  getAllSupplierCount,
  getAllSuppliers,
} from '../../../Actions/supplierActions';
import { useQuery } from '@tanstack/react-query';
import { useGlobalContext } from '../../../Contexts/GlobalContext';
import HeaderPagination from '../../../components/UiComponents/HeaderPagination';
import Pagination from '../../../components/UiComponents/Pagination';

/* Yes / No pill with a status dot (replaces MUI Chip, which has no `dot` prop) */
const pillStyles = {
  success: {
    wrap: 'bg-green-50 text-green-700 border-green-200',
    dot: 'bg-green-500',
  },
  neutral: {
    wrap: 'bg-gray-100 text-gray-700 border-gray-200',
    dot: 'bg-gray-400',
  },
};

const StatusPill = ({ active }) => {
  const s = active ? pillStyles.success : pillStyles.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.wrap}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {active ? 'Yes' : 'No'}
    </span>
  );
};

const th =
  'px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-600';
const td = 'px-4 py-3.5 text-sm text-gray-700';

const SupplierPage = () => {
  const { user } = useGlobalContext();

  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState(
    () => localStorage.getItem('suppliers-tab') || 'active',
  );

  const [filters, setFilters] = useState({ search: '' });
  const [reset, setReset] = useState(false);
  const [page, setPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [debounceSearch, setDebounceSearch] = useState(filters?.search);

  const {
    data: supplierCountsData,
    isLoading: supplierCountsLoading,
    error: supplierCountsError,
  } = useQuery({
    queryKey: ['all-supplier-count', { orgId: user?.organization?._id, reset }],
    queryFn: () =>
      getAllSupplierCount({ orgId: user?.organization?._id, reset }),
    enabled: Boolean(user?.organization?._id),
  });
  // console.log({
  //   supplierCountsData,
  //   supplierCountsLoading,
  //   supplierCountsError,
  // });

  const {
    data: suppliersData,
    isLoading: suppliersDataLoading,
    error: suppliersDataError,
  } = useQuery({
    queryKey: [
      'suppliers',
      {
        rows: rowsPerPage,
        // page: page + 1,
        page: 1,
        reset,
        search: debounceSearch,
        tab: mainTab === 'active' ? '1' : '2',
        orgId: user?.organization?._id,
      },
    ],
    queryFn: () =>
      getAllSuppliers({
        rows: rowsPerPage,
        page: 1,
        reset,
        search: debounceSearch,
        tab: mainTab === 'active' ? '1' : '2',
        orgId: user?.organization?._id,
      }),
    enabled: Boolean(user?.organization?._id),
  });
  // console.log({
  //   suppliersData,
  //   suppliersDataLoading,
  //   suppliersDataError,
  // });

  const tabsList = [
    {
      icon: RiTeamLine,
      label: 'Live Suppliers',
      value: 'active',
      count: supplierCountsData?.data?.live || 0,
    },
    {
      icon: RiArchiveLine,
      label: 'Archived Suppliers',
      value: 'inactive',
      count: supplierCountsData?.data?.archive || 0,
    },
  ];

  const handleTab = (value) => {
    setMainTab(value);
    localStorage.setItem('suppliers-tab', value);
  };

  useEffect(() => {
    let timer = setTimeout(() => {
      setDebounceSearch(filters?.search);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.search]);
  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div role="tablist" className="flex gap-6 border-b border-gray-200">
        {tabsList?.map(({ icon: Icon, label, value, count }) => {
          const active = mainTab === value;
          return (
            <button
              key={value}
              role="tab"
              aria-selected={active}
              onClick={() => handleTab(value)}
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-semibold transition-colors ${
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <Icon size={18} />
              {label}
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  active
                    ? 'bg-gray-100 text-primary'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      {(suppliersData?.pagination?.totalDocuments > 0 || debounceSearch) && (
        <div className="relative w-full max-w-sm">
          <RiSearchLine
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
          />
          <input
            type="search"
            value={filters?.search}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search: e.target.value }))
            }
            placeholder="Search suppliers"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-500 focus:border-primary focus:outline-none focus:ring-4 focus:ring-gray-100"
          />
        </div>
      )}
      <div className="space-y-2">
        <HeaderPagination
          date={suppliersData?.lastFetched || ''}
          loading={suppliersDataLoading}
          showReset={true}
          setReset={setReset}
          pagination={suppliersData?.pagination}
          setPage={setPage}
          page={page}
        />
        {/* Content */}
        {suppliersDataLoading ? (
          <SurveyTableLoader rowCount={10} columnCount={5} />
        ) : suppliersDataError ? (
          <div className="flex h-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50">
            <RiErrorWarningLine size={28} className="text-red-500" />
            <h3 className="text-sm font-semibold text-gray-900">
              Something went wrong
            </h3>
            <p className="text-sm text-gray-600">
              We couldn't load your suppliers. Please try again.
            </p>
          </div>
        ) : suppliersData?.data?.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 py-12">
            <NoData
              msg={
                <p>
                  Try Creating a{' '}
                  <span
                    className="text-[#0226BE] font-semibold cursor-pointer"
                    onClick={() => navigate('/suppliers/create-new-supplier')}
                  >
                    New supplier
                  </span>
                  .
                </p>
              }
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="containerScroll w-full overflow-x-auto">
              <table
                className="w-full whitespace-nowrap text-left"
                id="table-to-xls-DATA"
              >
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className={`${th} min-w-[260px]`}>Supplier</th>
                    <th className={th}>Global Redirects</th>
                    <th className={th}>Server Callbacks</th>
                    <th className={`${th} min-w-[260px]`}>Created By</th>
                    <th className={th}>Creation Date</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {suppliersData?.data?.map((item, ind) => (
                    <tr
                      key={item?._id || ind}
                      onClick={() => navigate(`${item?._id}/setup`)}
                      className="cursor-pointer transition-colors hover:bg-gray-50"
                    >
                      <td className={td}>
                        <PersonInfo
                          name={item?.supplierName || ''}
                          email={item?.email || ''}
                        />
                      </td>
                      <td className={td}>
                        <StatusPill active={item?.globalRedirects} />
                      </td>
                      <td className={td}>
                        <StatusPill active={item?.serverCallback} />
                      </td>
                      <td className={td}>
                        <PersonInfo
                          name={item?.createdByDetails?.name || ''}
                          email={item?.createdByDetails?.email || ''}
                        />
                      </td>
                      <td className={td}>
                        {getFormattedDateWithTime(item?.createdAt || '')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}

            {suppliersData?.pagination?.totalPages > 1 && (
              <Pagination
                page={page + 1}
                setPage={setPage}
                rows={rowsPerPage}
                setRows={setRowsPerPage}
                totalDocuments={suppliersData?.pagination?.totalDocuments || 0}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierPage;
